// A lightweight date formatter over the tokens the backend's AppSettings choices expose, with no library.

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const pad2 = (n) => String(n).padStart(2, '0');

const partsInTimezone = (date, timezone) => {
  // Use Intl.DateTimeFormat to get wall-clock parts in the requested zone.
  try {
    const dtf = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone || 'UTC',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
    const map = {};
    for (const p of dtf.formatToParts(date)) {
      if (p.type !== 'literal') map[p.type] = p.value;
    }
    return {
      year: Number(map.year),
      month: Number(map.month),
      day: Number(map.day),
      hour: Number(map.hour === '24' ? '00' : map.hour),
      minute: Number(map.minute),
      second: Number(map.second),
    };
  } catch {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
    };
  }
};

export function formatDateWith(date, dateFormat, timezone) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const p = partsInTimezone(d, timezone);
  return (dateFormat || 'DD/MM/YYYY')
    .replace('YYYY', String(p.year))
    .replace('MMM', MONTH_SHORT[p.month - 1])
    .replace('MM', pad2(p.month))
    .replace('DD', pad2(p.day));
}

export function formatTimeWith(date, timeFormat, timezone) {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const p = partsInTimezone(d, timezone);
  const ampm = p.hour >= 12 ? 'PM' : 'AM';
  const hour12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return (timeFormat || 'HH:mm')
    .replace('HH', pad2(p.hour))
    .replace('hh', pad2(hour12))
    .replace('mm', pad2(p.minute))
    .replace('ss', pad2(p.second))
    .replace('A', ampm);
}

export function formatDateTimeWith(date, dateFormat, timeFormat, timezone) {
  if (!date) return '';
  return `${formatDateWith(date, dateFormat, timezone)} ${formatTimeWith(date, timeFormat, timezone)}`;
}

// The Mauritian rupee is the suite default, so an unconfigured page still renders MUR rather than a bare number.
const DEFAULT_CURRENCY_SYMBOL = 'Rs';

export function formatCurrencyWith(amount, currency) {
  if (amount === null || amount === undefined || amount === '') return '';
  const num = Number(amount);
  if (Number.isNaN(num)) return String(amount);
  const symbol = currency?.symbol || currency?.code || DEFAULT_CURRENCY_SYMBOL;
  const formatted = num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${symbol} ${formatted}`;
}
